import React, { useEffect, useState } from "react";
import {
  Table,
  Select,
  Pagination,
  Spin,
  Modal,
  Button,
  message,
  Input,
  Typography,
  Alert,
  Row,
  Col,
  Card,
} from "antd";
import axios from "axios";
import { config } from "../../config";
import { LoadingOutlined } from "@ant-design/icons";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "./Infografik.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const { Title, Text } = Typography;
const { Search } = Input;

const CACHE_KEY = "infografik_thumbnails_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 menit

const Infografik = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const [selectedSubjek, setSelectedSubjek] = useState("Semua");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const uniqueSubjects = ["Semua", ...new Set(data.map((item) => item.subjek))];

  const filteredBySubject = filtered.filter((item) =>
    selectedSubjek === "Semua" ? true : item.subjek === selectedSubjek
  );

  const paginatedData = filteredBySubject.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Fungsi helper untuk cache
  const loadCachedThumbnails = () => {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const now = Date.now();
      if (now - parsed.timestamp > CACHE_DURATION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed.thumbs || {};
    } catch (e) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const saveThumbnailsToCache = (thumbsMap) => {
    const payload = {
      timestamp: Date.now(),
      thumbs: thumbsMap,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  };

  const getPdfThumbnail = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.0 });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, "image/png");
      });
    } catch (error) {
      console.error("Gagal generate thumbnail PDF:", pdfUrl, error);
      return null;
    }
  };

  useEffect(() => {
    const fetchInfografik = async () => {
      try {
        const currentURL = window.location.href;
        const res = await axios.get(
          `${config.BASE_URL}organization/findContentOrganization?namaDomain=${currentURL}`
        );

        const InfografikList = res.data?.data?.infografis || [];

        if (InfografikList.length === 0) {
          setError("Belum ada infografik desa cantik.");
          setLoading(false);
          return;
        }

        setData(InfografikList);
        setFiltered(InfografikList);

        // cek cache thumbnail
        const cached = loadCachedThumbnails();
        if (cached) {
          setThumbnails(cached);
          setLoading(false);
          return;
        }

        // generate thumbnail pdf
        const thumbPromises = InfografikList.map(async (item) => {
          if (!item?.file) return null;
          const pdfUrl = `${config.BASE_URL_FILE}${item.file}`;
          const thumb = await getPdfThumbnail(pdfUrl);
          return {
            id: item.infografikDesaCantikId,
            thumb,
          };
        });

        const thumbResults = await Promise.all(thumbPromises);
        const thumbsMap = {};
        thumbResults.forEach((res) => {
          if (res && res.thumb) {
            thumbsMap[res.id] = res.thumb;
          }
        });

        setThumbnails(thumbsMap);
        saveThumbnailsToCache(thumbsMap);
      } catch (err) {
        console.error("Gagal ambil data:", err);
        setError("Terjadi kesalahan saat mengambil data.");
      } finally {
        setLoading(false);
      }
    };

    fetchInfografik();
  }, []);

  const onSearch = (value) => {
    setKeyword(value);
    const lower = value.toLowerCase();
    const result = data.filter((item) =>
      item.subjek?.toLowerCase().includes(lower)
    );
    setFiltered(result);
  };

  const downloadPdf = (url, subjek) => {
    const link = document.createElement("a");
    link.href = url;
    const safeFilename = `${subjek}`.replace(/[<>:"/\\|?*]+/g, "").trim();
    link.setAttribute("download", `${safeFilename || "Infografik"}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewPdf = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setPdfModalVisible(true);
    } catch (error) {
      message.error("Gagal memuat file PDF");
    }
  };

  return (
    <div className="infografik-wrapper">
      <Title level={2} style={{ textAlign: "center", marginBottom: 20 }}>
        Infografis Desa Cantik
      </Title>

      <Row gutter={24}>
        <Col xs={24} sm={6} md={5}>
          <div className="subject-list">
            <Title level={5}>Subjek</Title>
            <ul className="subject-ul">
              {uniqueSubjects.map((subject) => (
                <li
                  key={subject}
                  className={subject === selectedSubjek ? "active" : ""}
                  onClick={() => {
                    setSelectedSubjek(subject);
                    setPage(1);
                  }}
                >
                  {subject}
                </li>
              ))}
            </ul>
          </div>
        </Col>

        <Col xs={24} sm={18} md={19}>
          {loading ? (
            <div className="infografik-loading">
              <Spin
                indicator={<LoadingOutlined className="orange-spin" spin />}
                size="large"
              />
            </div>
          ) : error ? (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ maxWidth: 600, margin: "0 auto" }}
            />
          ) : (
            <>
              <Row gutter={[16, 16]}>
                {paginatedData.length === 0 ? (
                  <Col span={24}>
                    <Text type="secondary">
                      Tidak ada infografis ditemukan.
                    </Text>
                  </Col>
                ) : (
                  paginatedData.map((item) => {
                    const cover = thumbnails[item.infografikDesaCantikId];
                    return (
                      <Col
                        key={item.infografikDesaCantikId}
                        xs={24}
                        sm={12}
                        md={8}
                      >
                        <Card
                          hoverable
                          onClick={() =>
                            previewPdf(
                              `${config.BASE_URL}getFile?pathFile=${item.file}`
                            )
                          }
                          className="infografik-card"
                          cover={
                            <div className="card-cover">
                              <img
                                src={cover || "/placeholder-cover.png"}
                                alt="PDF Cover"
                              />
                            </div>
                          }
                        >
                          <Title level={5} style={{ margin: "8px 0" }}>
                            {item.subjek || "subjek tidak tersedia"}
                          </Title>
                          <Button
                            block
                            type="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadPdf(
                                `${config.BASE_URL_FILE}${item.file}`,
                                item.subjek || "Infografik"
                              );
                            }}
                          >
                            Unduh Infografis
                          </Button>
                        </Card>
                      </Col>
                    );
                  })
                )}
              </Row>

              <div style={{ textAlign: "center", marginTop: 24 }}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={filteredBySubject.length}
                  onChange={(p) => setPage(p)}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </Col>
      </Row>

      <Modal
        title="Preview Infografik"
        open={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          setPdfBlobUrl(null);
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0 }}
        destroyOnClose
      >
        {pdfBlobUrl && (
          <iframe
            src={pdfBlobUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </div>
  );
};

export default Infografik;
